/**
 * 工具栏图标（把 Unicode 占位符换成真正的矢量图标）。
 *
 * 线条来自 Lucide（https://lucide.dev，ISC 许可证，与 AGPL-3.0 兼容）——
 * 具体取用哪些图标、每个图标改动了什么，记在 `THIRD_PARTY_LICENSES.md`。
 * 这里不引入 `lucide`/`lucide-static` 作为运行时依赖：图标数量少，
 * 手工把 path/circle/line 数据抄进一份纯数据表，比引入一个整包依赖
 * 更符合 CLAUDE.md 规则 7（依赖只在真正的活需要时引入）。
 *
 * 渲染约定与 Lucide 原版一致：24×24 视口、`currentColor` 描边、
 * `stroke-width=2`、圆头圆角——所有工具图标视觉上同一套语言。
 */
import { For, type JSX } from 'solid-js';

export type IconName =
  | 'move'
  | 'square-dashed'
  | 'lasso'
  | 'circle-dashed'
  | 'pentagon'
  | 'square'
  | 'circle'
  | 'minus'
  | 'wand'
  | 'paint-bucket'
  | 'wand-sparkles'
  | 'crop'
  | 'frame'
  | 'grid-3x3'
  | 'pipette'
  | 'bandage'
  | 'paintbrush'
  | 'stamp'
  | 'eraser'
  | 'gradient'
  | 'sun-dim'
  | 'flame'
  | 'contrast'
  | 'eye'
  | 'puzzle'
  | 'pen-tool'
  | 'type'
  | 'zoom-in'
  | 'hand'
  | 'arrow-left-right'
  | 'droplet'
  | 'sparkle'
  | 'wind'
  | 'folder'
  | 'plus'
  | 'mask'
  | 'trash';

interface IconDef {
  readonly paths?: readonly string[];
  readonly circles?: ReadonlyArray<{ readonly cx: number; readonly cy: number; readonly r: number }>;
  readonly lines?: ReadonlyArray<{
    readonly x1: number;
    readonly y1: number;
    readonly x2: number;
    readonly y2: number;
  }>;
  readonly rects?: ReadonlyArray<{
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly rx?: number;
  }>;
  /**
   * 实心填充的路径（`fill: currentColor`），跟其余描边形状叠在一起画。
   * 只有 `gradient` 用得上——渐变工具在 Photoshop 里就是一块"深→浅"
   * 的色卡样式，纯描边线条画不出"渐变感"，这里对角一半填实来近似它，
   * 其余图标全部保持纯描边，不受影响。
   */
  readonly filledPaths?: readonly string[];
}

const ICONS: Readonly<Record<IconName, IconDef>> = {
  move: {
    paths: [
      'M12 2v20',
      'm15 19-3 3-3-3',
      'm19 9 3 3-3 3',
      'M2 12h20',
      'm5 9-3 3 3 3',
      'm9 5 3-3 3 3',
    ],
  },
  'square-dashed': {
    paths: [
      'M5 3a2 2 0 0 0-2 2',
      'M19 3a2 2 0 0 1 2 2',
      'M21 19a2 2 0 0 1-2 2',
      'M5 21a2 2 0 0 1-2-2',
      'M9 3h1',
      'M9 21h1',
      'M14 3h1',
      'M14 21h1',
      'M3 9v1',
      'M21 9v1',
      'M3 14v1',
      'M21 14v1',
    ],
  },
  lasso: {
    paths: ['M3.704 14.467a10 8 0 1 1 3.115 2.375', 'M7 22a5 5 0 0 1-2-3.994'],
    circles: [{ cx: 5, cy: 16, r: 2 }],
  },
  'circle-dashed': {
    paths: [
      'M10.1 2.182a10 10 0 0 1 3.8 0',
      'M13.9 21.818a10 10 0 0 1-3.8 0',
      'M17.609 3.721a10 10 0 0 1 2.69 2.7',
      'M2.182 13.9a10 10 0 0 1 0-3.8',
      'M20.279 17.609a10 10 0 0 1-2.7 2.69',
      'M21.818 10.1a10 10 0 0 1 0 3.8',
      'M3.721 6.391a10 10 0 0 1 2.7-2.69',
      'M6.391 20.279a10 10 0 0 1-2.69-2.7',
    ],
  },
  pentagon: {
    paths: [
      'M10.83 2.38a2 2 0 0 1 2.34 0l8 5.74a2 2 0 0 1 .73 2.25l-3.04 9.26a2 2 0 0 1-1.9 1.37H7.04a2 2 0 0 1-1.9-1.37L2.1 10.37a2 2 0 0 1 .73-2.25z',
    ],
  },
  square: {
    rects: [{ x: 3, y: 3, width: 18, height: 18, rx: 2 }],
  },
  circle: {
    circles: [{ cx: 12, cy: 12, r: 10 }],
  },
  minus: {
    paths: ['M5 12h14'],
  },
  wand: {
    paths: [
      'M15 4V2',
      'M15 16v-2',
      'M8 9h2',
      'M20 9h2',
      'M17.8 11.8 19 13',
      'M15 9h.01',
      'M17.8 6.2 19 5',
      'm3 21 9-9',
      'M12.2 6.2 11 5',
    ],
  },
  'paint-bucket': {
    paths: [
      'M11 7 6 2',
      'M18.992 12H2.041',
      'M21.145 18.38A3.34 3.34 0 0 1 20 16.5a3.3 3.3 0 0 1-1.145 1.88c-.575.46-.855 1.02-.855 1.595A2 2 0 0 0 20 22a2 2 0 0 0 2-2.025c0-.58-.285-1.13-.855-1.595',
      'm8.5 4.5 2.148-2.148a1.205 1.205 0 0 1 1.704 0l7.296 7.296a1.205 1.205 0 0 1 0 1.704l-7.592 7.592a3.615 3.615 0 0 1-5.112 0l-3.888-3.888a3.615 3.615 0 0 1 0-5.112L5.67 7.33',
    ],
  },
  'wand-sparkles': {
    paths: [
      'm21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72',
      'm14 7 3 3',
      'M5 6v4',
      'M19 14v4',
      'M10 2v2',
      'M7 8H3',
      'M21 16h-4',
      'M11 3H9',
    ],
  },
  crop: {
    paths: ['M6 2v14a2 2 0 0 0 2 2h14', 'M18 22V8a2 2 0 0 0-2-2H2'],
  },
  frame: {
    lines: [
      { x1: 22, y1: 6, x2: 2, y2: 6 },
      { x1: 22, y1: 18, x2: 2, y2: 18 },
      { x1: 6, y1: 2, x2: 6, y2: 22 },
      { x1: 18, y1: 2, x2: 18, y2: 22 },
    ],
  },
  'grid-3x3': {
    rects: [{ x: 3, y: 3, width: 18, height: 18, rx: 2 }],
    paths: ['M3 9h18', 'M3 15h18', 'M9 3v18', 'M15 3v18'],
  },
  pipette: {
    paths: [
      'm12 9-8.414 8.414A2 2 0 0 0 3 18.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 3.828 21h1.344a2 2 0 0 0 1.414-.586L15 12',
      'm18 9 .4.4a1 1 0 1 1-3 3l-3.8-3.8a1 1 0 1 1 3-3l.4.4 3.4-3.4a1 1 0 1 1 3 3z',
      'm2 22 .414-.414',
    ],
  },
  bandage: {
    paths: ['M10 10.01h.01', 'M10 14.01h.01', 'M14 10.01h.01', 'M14 14.01h.01', 'M18 6v12', 'M6 6v12'],
    rects: [{ x: 2, y: 6, width: 20, height: 12, rx: 2 }],
  },
  paintbrush: {
    paths: [
      'm14.622 17.897-10.68-2.913',
      'M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z',
      'M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15',
    ],
  },
  stamp: {
    paths: [
      'M14 13V8.5C14 7 15 7 15 5a3 3 0 0 0-6 0c0 2 1 2 1 3.5V13',
      'M20 15.5a2.5 2.5 0 0 0-2.5-2.5h-11A2.5 2.5 0 0 0 4 15.5V17a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1z',
      'M5 22h14',
    ],
  },
  eraser: {
    paths: [
      'M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21',
      'm5.082 11.09 8.828 8.828',
    ],
  },
  gradient: {
    rects: [{ x: 3, y: 3, width: 18, height: 18, rx: 3 }],
    // 从左上到右下的对角线，把方块劈成"实心/空心"两半——近似 PS 渐变工具
    // 图标那块深→浅的色卡，比两个交叠圆圈（原来的 blend 图标）更贴题：
    // 那个图案其实是混合模式/交集的通用符号，容易被认成"混合"而不是"渐变"。
    paths: ['M4.5 4.5 19.5 19.5'],
    filledPaths: ['M4.5 4.5 L4.5 19.5 L19.5 19.5 Z'],
  },
  'sun-dim': {
    circles: [{ cx: 12, cy: 12, r: 4 }],
    paths: [
      'M12 4h.01',
      'M20 12h.01',
      'M12 20h.01',
      'M4 12h.01',
      'M17.657 6.343h.01',
      'M17.657 17.657h.01',
      'M6.343 17.657h.01',
      'M6.343 6.343h.01',
    ],
  },
  flame: {
    paths: ['M12 3q1 4 4 6.5t3 5.5a1 1 0 0 1-14 0 5 5 0 0 1 1-3 1 1 0 0 0 5 0c0-2-1.5-3-1.5-5q0-2 2.5-4'],
  },
  contrast: {
    circles: [{ cx: 12, cy: 12, r: 10 }],
    paths: ['M12 18a6 6 0 0 0 0-12v12z'],
  },
  eye: {
    paths: [
      'M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0',
    ],
    circles: [{ cx: 12, cy: 12, r: 3 }],
  },
  puzzle: {
    paths: [
      'M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z',
    ],
  },
  'pen-tool': {
    paths: [
      'M15.707 21.293a1 1 0 0 1-1.414 0l-1.586-1.586a1 1 0 0 1 0-1.414l5.586-5.586a1 1 0 0 1 1.414 0l1.586 1.586a1 1 0 0 1 0 1.414z',
      'm18 13-1.375-6.874a1 1 0 0 0-.746-.776L3.235 2.028a1 1 0 0 0-1.207 1.207L5.35 15.879a1 1 0 0 0 .776.746L13 18',
      'm2.3 2.3 7.286 7.286',
    ],
    circles: [{ cx: 11, cy: 11, r: 2 }],
  },
  type: {
    paths: ['M12 4v16', 'M4 7V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2', 'M9 20h6'],
  },
  'zoom-in': {
    circles: [{ cx: 11, cy: 11, r: 8 }],
    lines: [
      { x1: 21, y1: 21, x2: 16.65, y2: 16.65 },
      { x1: 11, y1: 8, x2: 11, y2: 14 },
      { x1: 8, y1: 11, x2: 14, y2: 11 },
    ],
  },
  hand: {
    paths: [
      'M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2',
      'M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2',
      'M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8',
      'M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15',
    ],
  },
  'arrow-left-right': {
    paths: ['M8 3 4 7l4 4', 'M4 7h16', 'm16 21 4-4-4-4', 'M20 17H4'],
  },
  droplet: {
    // 模糊工具：一滴水，视觉直觉跟"往湿的方向抹开"一致
    paths: ['M12 2s6.5 7 6.5 12A6.5 6.5 0 0 1 5.5 14C5.5 9 12 2 12 2z'],
  },
  sparkle: {
    // 锐化工具：四角星，跟"边缘变尖/变清晰"的直觉一致
    paths: ['M12 3l1.8 6.2L20 11l-6.2 1.8L12 19l-1.8-6.2L4 11l6.2-1.8z'],
  },
  wind: {
    // 涂抹工具：几道飘动的曲线，跟"把颜色刮出一道痕迹"的直觉一致
    paths: ['M9.5 4.5a2.5 2.5 0 1 1 2 4H2', 'M17 8a3 3 0 1 1 2.5 4.5H2', 'M12.5 19.5A2.5 2.5 0 1 0 15 15H2'],
  },
  folder: {
    // 图层面板底部工具栏：新建组
    paths: ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'],
  },
  plus: {
    // 图层面板底部工具栏：新建图层
    paths: ['M12 5v14', 'M5 12h14'],
  },
  mask: {
    // 图层面板底部工具栏：添加图层蒙版——一个方框套一个圆框，跟真实
    // Photoshop 的蒙版图标同一构图直觉（矢量蒙版是方形，图层蒙版是圆形）
    rects: [{ x: 3, y: 3, width: 18, height: 18, rx: 2 }],
    circles: [{ cx: 12, cy: 12, r: 5 }],
  },
  trash: {
    // 图层面板底部工具栏：删除图层
    paths: ['M3 6h18', 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6'],
    lines: [
      { x1: 10, y1: 11, x2: 10, y2: 17 },
      { x1: 14, y1: 11, x2: 14, y2: 17 },
    ],
  },
};

export interface IconProps {
  readonly name: IconName;
  /** 边长（像素）。缺省 20——工具栏按钮的常规图标尺寸。 */
  readonly size?: number;
}

export function Icon(props: IconProps): JSX.Element {
  // 必须是个函数，不能在组件体里就地解出来：Solid 组件函数只跑一次，
  // `ICONS[props.name]` 若在这里被求值一次就定死了，工具栏长按分组换
  // 变体时图标名会变（同一个 <Icon> 实例复用），但形状数据永远还是挂载
  // 那一刻的——这正是"选完变体图标不跟着变"那个 bug 的根。包成函数、
  // 在下面的 JSX 表达式里调用，才会随 props.name 变化重新求值。
  const def = (): IconDef => ICONS[props.name];
  return (
    <svg
      width={props.size ?? 20}
      height={props.size ?? 20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <For each={def().paths ?? []}>{(d) => <path d={d} />}</For>
      <For each={def().circles ?? []}>{(c) => <circle cx={c.cx} cy={c.cy} r={c.r} />}</For>
      <For each={def().lines ?? []}>{(l) => <line x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} />}</For>
      <For each={def().rects ?? []}>
        {(r) => <rect x={r.x} y={r.y} width={r.width} height={r.height} rx={r.rx ?? 0} />}
      </For>
      <For each={def().filledPaths ?? []}>{(d) => <path d={d} fill="currentColor" stroke="none" />}</For>
    </svg>
  );
}
